import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { adminApi, userApi } from '../utils/api';
import { Shield, Users, Activity, UserPlus, Search, Users2, Wallet, Coins } from 'lucide-react';
import toast from 'react-hot-toast';

interface UserData {
  id: number;
  account: string;
  name: string;
  phone?: string;
  role: string;
  nt_balance: number;
  ms_balance: string;
  is_active: boolean;
  daily_limit: number;
  created_at: string;
}

export default function Admin() {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'stats' | 'users'>('stats');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    account: '',
    password: '',
    name: '',
    phone: '',
    role: 'student',
    initialNT: 0,
    initialMS: '0',
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const response = await adminApi.getStats();
      return response.data;
    },
    enabled: isAdmin,
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: async () => {
      const response = await adminApi.getUsers({ limit: 100 });
      return response.data.users as UserData[];
    },
    enabled: isAdmin && activeTab === 'users',
  });

  const createUserMutation = useMutation({
    mutationFn: () =>
      adminApi.createUser({
        account: newUser.account,
        password: newUser.password,
        name: newUser.name,
        phone: newUser.phone || undefined,
        role: newUser.role,
        initialNT: newUser.initialNT,
        initialMS: newUser.initialMS,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      toast.success('用戶已創建');
      setShowCreateForm(false);
      setNewUser({ account: '', password: '', name: '', phone: '', role: 'student', initialNT: 0, initialMS: '0' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || '創建失敗');
    },
  });

  const toggleUserMutation = useMutation({
    mutationFn: (id: number) => userApi.toggleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      toast.success('用戶狀態已更新');
    },
  });

  if (!isAdmin) {
    return (
      <div className="max-w-lg mx-auto mt-16">
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center">
          <Shield className="h-16 w-16 text-rose-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-rose-900 mb-2">無權限訪問</h2>
          <p className="text-rose-600">此頁面僅供管理員使用</p>
        </div>
      </div>
    );
  }

  const filteredUsers = users?.filter((u) =>
    u.account.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.account || !newUser.password || !newUser.name) {
      toast.error('請填寫所有必填欄位');
      return;
    }
    createUserMutation.mutate();
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center shadow-lg">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">管理後台</h1>
            <p className="text-slate-500">系統管理與用戶控制</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('stats')}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
            activeTab === 'stats'
              ? 'bg-slate-900 text-white shadow-lg'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          <Activity className="h-5 w-5" />
          統計概覽
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
            activeTab === 'users'
              ? 'bg-slate-900 text-white shadow-lg'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          <Users className="h-5 w-5" />
          用戶管理
        </button>
      </div>

      {activeTab === 'stats' ? (
        <>
          {statsLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-10 h-10 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-slate-500">載入統計資料...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Users2 className="h-5 w-5" />
                  </div>
                  <span className="text-blue-100 font-medium">總用戶數</span>
                </div>
                <p className="text-4xl font-bold">{stats?.users?.total || 0}</p>
                <p className="text-blue-100 text-sm mt-2">活躍: {stats?.users?.active || 0}</p>
              </div>

              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Activity className="h-5 w-5" />
                  </div>
                  <span className="text-emerald-100 font-medium">今日交易</span>
                </div>
                <p className="text-4xl font-bold">{stats?.todayTransactions || 0}</p>
                <p className="text-emerald-100 text-sm mt-2">筆</p>
              </div>

              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Wallet className="h-5 w-5" />
                  </div>
                  <span className="text-amber-100 font-medium">總新臺幣</span>
                </div>
                <p className="text-3xl font-bold">${(stats?.totalNT || 0).toLocaleString()}</p>
                <p className="text-amber-100 text-sm mt-2">流通量</p>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Coins className="h-5 w-5" />
                  </div>
                  <span className="text-purple-100 font-medium">總數資幣</span>
                </div>
                <p className="text-3xl font-bold">{parseInt(stats?.totalMS || '0').toLocaleString()}</p>
                <p className="text-purple-100 text-sm mt-2">MS∮</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-all"
                placeholder="搜尋帳號或姓名..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-2 px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-medium transition-all shadow-lg"
            >
              <UserPlus className="h-5 w-5" />
              新增用戶
            </button>
          </div>

          {showCreateForm && (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 mb-6 animate-slide-up">
              <h3 className="text-lg font-bold text-slate-900 mb-4">新增用戶</h3>
              <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-all"
                  placeholder="帳號 *"
                  value={newUser.account}
                  onChange={(e) => setNewUser({ ...newUser, account: e.target.value })}
                  required
                />
                <input
                  type="password"
                  className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-all"
                  placeholder="密碼 *"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                />
                <input
                  type="text"
                  className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-all"
                  placeholder="姓名 *"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                />
                <input
                  type="tel"
                  className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-all"
                  placeholder="電話"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                />
                <select
                  className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-all bg-white"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="student">學生</option>
                  <option value="teacher">老師</option>
                  <option value="admin">管理員</option>
                </select>
                <div className="flex gap-3">
                  <input
                    type="number"
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-all"
                    placeholder="初始 NTD"
                    value={newUser.initialNT}
                    onChange={(e) => setNewUser({ ...newUser, initialNT: parseFloat(e.target.value) || 0 })}
                  />
                  <button
                    type="submit"
                    disabled={createUserMutation.isPending}
                    className="px-6 py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white font-semibold rounded-xl transition-all"
                  >
                    {createUserMutation.isPending ? '創建中...' : '創建'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
            {usersLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-10 h-10 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">帳號</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">姓名</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">角色</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-slate-600">新臺幣餘額</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-slate-600">數資幣餘額</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">狀態</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers?.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 font-medium text-slate-900">{u.account}</td>
                        <td className="px-6 py-4 text-slate-600">{u.name}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                            u.role === 'admin' ? 'bg-rose-100 text-rose-700' :
                            u.role === 'teacher' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {u.role === 'admin' ? '管理員' : u.role === 'teacher' ? '老師' : '學生'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-slate-900">${u.nt_balance.toLocaleString()}</td>
                        <td className="px-6 py-4 text-right font-medium text-slate-900">
                          {parseInt(u.ms_balance).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => toggleUserMutation.mutate(u.id)}
                            className={`inline-flex px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                              u.is_active
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-rose-100 text-rose-700 hover:bg-rose-200'
                            }`}
                          >
                            {u.is_active ? '啟用' : '停用'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
